import { useMemo } from "react";
import { bool, number, string, object } from "prop-types";
import { observer } from "mobx-react";
import { roundFloat, Rounding } from "@repo/utils";
import { CELL_WIDTHS } from "@utils";
import { useEstimateStructure, useWorkTypePermitChecker, ValueCell, CollapseValueCell } from "@tools";
import { makeStyles } from "@material-ui/styles";

export const BudgetTrackingTaskWorkType = observer(({
  element,
  workType,
  editable,
  displayContent,
  emptyValue,
  ...valueCellProps
}) => {
  
  const classes = useStyle();
  
  const { editionGrant } = useWorkTypePermitChecker(workType.id);
  const structure = useEstimateStructure();
  const {
    useMinMax,
    modifier,
    usedDecimals,
  } = structure.settings;

  const structureWorkType = structure.getWorkTypeById(workType.id);
  const visible = !structure.isWorkTypeOff(workType.id);

  const values = element.getTotalHoursPerWorkType(workType.id);
  const btValues = element.getTotalBTHoursPerWorkType(workType.id);
  const btHourDiffs = element.getBTHourDiff(workType.id);
  const btHourDiffPercents = element.getBTHourDiffPercent(workType.id);

  const value = useMemo(
    () =>
      emptyValue
        ? undefined
        : roundFloat(
            (useMinMax ? values.max || 0 : values.avg || 0) / modifier,
            Rounding.FULL,
            false,
            usedDecimals
          ),
    [ values, useMinMax, usedDecimals, emptyValue ]
  );
  const btValue = useMemo(
    () => (btValues ? btValues / modifier : 0),
    [btValues, useMinMax]
  );
  const btHourDiff = useMemo(
    () =>  emptyValue
    ? undefined
    : roundFloat(
        (useMinMax ? btHourDiffs.max || 0 : btHourDiffs.avg || 0) / modifier,
        Rounding.FULL,
        false,
        usedDecimals
      ),
    [btValues, useMinMax, useMinMax]
  );

  const btHourDiffPercent = useMemo(
    () =>  emptyValue
    ? undefined
    : roundFloat(
        (useMinMax ? btHourDiffPercents.max || 0 : btHourDiffPercents.avg || 0),
        Rounding.FULL,
        false,
        usedDecimals
      ),
    [btValues, useMinMax, useMinMax]
  );

  const allowEdition = useMemo(
    () => editable && editionGrant,
    [editable, editionGrant]
  );

  const colorText = useMemo(() => (
    btHourDiff > 0 ? classes.rowAccepted : classes.rowDanger
  ), [value, btHourDiff]);

  return (
    <CollapseValueCell
      childrenWidth={CELL_WIDTHS.VALUE.XSMALL}
      mainCellWidth={CELL_WIDTHS.VALUE.LARGE}
      isOpened={structureWorkType.btOpened}
      visible={visible}
      cell={
        <ValueCell
        {...valueCellProps}
        displayContent={displayContent && (!emptyValue || allowEdition)}
        displayMinMaxSeparator={!emptyValue}
        value={emptyValue ? undefined : btHourDiffPercent || 0}
        size="medium"
        showDecimals
        textPresetClass={colorText}
        visible
        symbolEnd="%"
      />
      }
    >
      <ValueCell
        {...valueCellProps}
        displayContent={displayContent && (!emptyValue || allowEdition)}
        displayMinMaxSeparator={!emptyValue}
        value={emptyValue ? undefined : value}
        size="xsmall"
        showDecimals
        visible
      />
      <ValueCell
        {...valueCellProps}
        displayContent={displayContent && (!emptyValue || allowEdition)}
        displayMinMaxSeparator={!emptyValue}
        value={emptyValue ? undefined : btValue}
        size="xsmall"
        showDecimals
        visible
        textPresetClass={colorText}
      />
      <ValueCell
        {...valueCellProps}
        displayContent={displayContent && (!emptyValue || allowEdition)}
        displayMinMaxSeparator={!emptyValue}
        value={emptyValue ? undefined : btHourDiff}
        size="xsmall"
        showDecimals
        visible
        textPresetClass={colorText}
        showValueSign
      />
      <ValueCell
        {...valueCellProps}
        displayContent={displayContent && (!emptyValue || allowEdition)}
        displayMinMaxSeparator={!emptyValue}
        value={emptyValue ? undefined : btHourDiffPercent || 0}
        size="xsmall"
        showDecimals
        visible
        textPresetClass={colorText}
        symbolEnd="%"
        showValueSign
      />
    </CollapseValueCell>
  );
});

BudgetTrackingTaskWorkType.propTypes = {
  element: object.isRequired,
  textPresetClass: string,
  tableDepth: number,
  workType: object.isRequired,
  emptyValue: bool,
  editable: bool,
  displayContent: bool,
};

const useStyle = makeStyles((theme) => ({
  cell: {
    zIndex: 2,
  },
  rowDanger: {
    color: theme.props.success,
  },
  rowAccepted: {
    color: theme.props.error.main,
  },
  default: {
    color: "#727482",
  },
}));
